<!-- App shell, design handoff §0. Two deliberate departures from the prototype:
     it keeps the sidebar-collapse button the redesign drops (the handoff's own
     note says to add it back if it is still wanted, and it works), and it keeps
     the real `logo.png` instead of the prototype's gradient placeholder tile.

     The dark-mode switch the shell used to carry beside it is gone on request.
     Removing the button alone would have stranded anyone whose `localStorage
     .theme` already said `dark` in a theme with no way out, so the shell no
     longer reads that key at all and always renders light. `theme.css` keeps its
     `.dark` rules: nothing sets the class today, and they are what a future
     switch would need.

     `#page-actions` is the heading's right-hand slot. A screen's primary action
     ("+ Tạo phiếu nhập") belongs beside the page title, not buried in the header
     of the first card, but the pages own those buttons and their permissions —
     so they `<Teleport to="#page-actions" defer>` into it. `defer` is required:
     without it the target has not been inserted yet when the routed page
     mounts. -->
<template>
  <div class="app-shell" :class="{collapsed}">
    <aside class="sidebar">
      <RouterLink class="brand" to="/"><img class="brand-mark" src="/logo.png" alt="Mizo Sport"><span class="brand-text"><strong>Kho Mizo</strong><small>NPL &amp; BTP</small></span></RouterLink>
      <nav>
        <template v-for="g in groups" :key="g.key">
          <small class="nav-group">{{g.label}}</small>
          <RouterLink v-for="i in g.items" :key="i.name" :to="{name:i.name}">
            <NavIcon :name="i.icon"/><span>{{i.label}}</span>
            <!-- Hidden at 0 on purpose: a badge reading "0" is a thing to read
                 and dismiss on every single page load. -->
            <b v-if="i.name==='receipts'&&pendingReceipts" class="nav-badge">{{pendingReceipts}}</b>
          </RouterLink>
        </template>
      </nav>
      <div class="sidebar-note"><small>Quản lý kho NPL &amp; BTP</small><b>Phase 1</b></div>
    </aside>
    <div v-if="mobile" class="mobile-overlay" @click="mobile=false"></div>
    <section class="workspace">
      <header class="topbar">
        <button class="icon-button desktop-toggle" aria-label="Thu gọn thanh bên" @click="collapsed=!collapsed">☰</button>
        <button class="icon-button mobile-toggle" aria-label="Mở thanh bên" @click="mobile=!mobile">☰</button>
        <div class="topbar-spacer"></div>
        <div class="profile">
          <span class="avatar">{{initials}}</span>
          <div><strong>{{authStore.state.user?.full_name||'Quản trị viên'}}</strong><small>{{authStore.state.user?.role?.name||'Kho Mizo'}}</small></div>
          <button class="btn-link" @click="logout">Đăng xuất</button>
        </div>
      </header>
      <main>
        <div class="page-heading">
          <div>
            <div class="breadcrumb"><RouterLink to="/">Trang chủ</RouterLink><span>/</span><span>{{route.meta.title}}</span></div>
            <h1>{{route.meta.title}}</h1>
            <p v-if="route.meta.description" class="page-description">{{route.meta.description}}</p>
          </div>
          <div id="page-actions" class="page-actions"></div>
        </div>
        <RouterView/>
      </main>
      <footer>Hệ thống quản lý kho NPL &amp; BTP · Mizo Sport <span>© 2026</span></footer>
    </section>
  </div>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { authStore } from '@/stores/auth';
import { warehouseStore } from '@/stores/warehouse';
import { pendingReceiptsStore } from '@/stores/pending-receipts';
import { navGroups } from '@/utils/core.cjs';
import NavIcon from '@/components/display/NavIcon.vue';

const route = useRoute(); const router = useRouter();
const collapsed = ref(false); const mobile = ref(false);
const groups = computed(() => navGroups(authStore.permissionNames));
const initials = computed(() => (authStore.state.user?.full_name || 'MZ').split(' ').slice(-2).map((part) => part[0]).join(''));
const pendingReceipts = computed(() => pendingReceiptsStore.state.count);
const logout = () => { authStore.logout(); warehouseStore.reset(); pendingReceiptsStore.reset(); router.push('/login'); };

// Kho nạp ở đây chứ không ở công tắc topbar nữa (revision `015` bỏ công tắc):
// `pendingReceiptsStore.refresh()` đọc `selectedId`, nên không ai gọi `load()`
// thì badge đếm trên một kho `null` và luôn ra 0.
//
// `ReceiptsPage` cũng refresh sau mỗi lệnh vòng đời — đó là thứ giữ con số
// trung thực mà không phải polling.
onMounted(() => warehouseStore.load());
watch(() => warehouseStore.state.selectedId, () => pendingReceiptsStore.refresh(), { immediate: true });
</script>
